import { Component, OnInit, EventEmitter, Input, Output, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormControl } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';
import { DataLayerFiltersModel } from 'src/app/models/filters/implementations/pageFilters/dataLayerFilters.model';
import { AccordionComponent } from '../accordion/accordion.component';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { DataLayerHttpService } from 'src/app/services/httpServices/dataLayerHttpService/data-layer-http.service';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { AutocompleteApiComponent } from '../autocomplete-api/autocomplete-api.component';

const DEFAULT_DEBOUNCE = 1000;
const MIN_CHARS_FOR_FILTER = 3;

interface FilterInitConfig {
  control: UntypedFormControl;
  filterName: string;
  filteredValues: ISelectOption[];
  loadingSubject: BehaviorSubject<boolean>;
  filterType: FilterTypesEnum;
  transformDisplayName?: (text: string) => string;
}

@Component({
  selector: 'app-data-layer-vertical-filters',
  standalone: true,
  imports: [CommonModule, MatCheckboxModule, AccordionComponent, BeautifyDisplayStringPipe, AutocompleteApiComponent],
  templateUrl: './data-layer-vertical-filters.component.html',
  styleUrl: './data-layer-vertical-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataLayerVerticalFiltersComponent implements OnInit {
  @Input() public pageFilters!: DataLayerFiltersModel;
  @Input() public isFiltersMenuOpen = true;

  @Output() public filtersOnMenuStateChange = new EventEmitter<boolean>();
  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public optionsInsideCategories$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>(
    []
  );
  public expandedCategories: { [optionGroup: string]: boolean } = {};
  public selectedIds: Set<string> = new Set();
  public dynamicCategories: string[] = [];

  public searchActorValuesCtrl = new UntypedFormControl();
  public filteredActorValues: ISelectOption[] = [];
  public isActorLoading$ = new BehaviorSubject(false);
  public actorFilterEnum = FilterTypesEnum.Actor;

  public searchAgentValuesCtrl = new UntypedFormControl();
  public filteredAgentValues: ISelectOption[] = [];
  public isAgentLoading$ = new BehaviorSubject(false);
  public agentNameFilterEnum = FilterTypesEnum.AIAgentName;

  constructor(
    private readonly _destroyRef: DestroyRef,
    private readonly _dataLayerHttpService: DataLayerHttpService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe
  ) {}

  ngOnInit(): void {
    this.dynamicCategories = this.pageFilters.getDynamicFilters();
    this._initializeFilters();
    this._subscribeToFiltersUpdates();
    this._initActorFilter();
    this._initAgentFilter();
  }

  public expandClicked(categoryName: string, isExpanded: boolean): void {
    this.expandedCategories[categoryName] = isExpanded;
  }

  public onToggleClick(): void {
    this.filtersOnMenuStateChange.emit(!this.isFiltersMenuOpen);
  }

  public onSelect(optionId: string): void {
    if (!this.selectedIds.has(optionId)) {
      this.selectedIds.add(optionId);
    } else {
      this.selectedIds.delete(optionId);
    }
    this.optionOnSelection.emit(this._getSelectedOptions());
  }

  public actorSubmitted(options: ISelectOption[] = []): void {
    this.pageFilters.actorFilters.updateOptions(_.union(this.pageFilters.actorFilters.getOptions$().value, options));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), options));
  }

  public agentSubmitted(options: ISelectOption[] = []): void {
    this.pageFilters.agentFilters.updateOptions(_.union(this.pageFilters.agentFilters.getOptions$().value, options));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), options));
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIdArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters.getAllFiltersOptions().value, 'id');
    const selectedOptions = selectedIdArray.map((id: string) => optionsById[id]);
    return selectedOptions.filter((option) => typeof option !== 'undefined');
  }

  private _initializeFilters(): void {
    const allOptions = this.pageFilters.getAllFiltersOptions().value;
    const optionGroups = this._groupOptionsByCategory(allOptions);
    this.optionsInsideCategories$.next(optionGroups);

    this.selectedIds = new Set(this.pageFilters.getAllSelectedOptions().map((opt) => opt.id));

    optionGroups.forEach((group) => {
      if (group.displayName) {
        this.expandedCategories[group.displayName] = false;
      }
    });
  }

  private _subscribeToFiltersUpdates(): void {
    this.pageFilters
      .getAllFiltersOptions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        const optionGroups = this._groupOptionsByCategory(options);
        this.optionsInsideCategories$.next(optionGroups);
      });

    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const newSelectedIds: Set<string> = new Set();
      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        newSelectedIds.add(option.id);
      });
      this.selectedIds = newSelectedIds;
    });
  }

  private _groupOptionsByCategory(options: ISelectOption[]): ISelectOptionGroups[] {
    const grouped = _.groupBy(options, 'categoryName');
    return Object.entries(grouped)
      .filter(([categoryName]) => !this.dynamicCategories.includes(categoryName))
      .map(([categoryName, opts]) => {
        const selectedCount = opts.filter((opt) => this.selectedIds.has(opt.id)).length;
        return {
          displayName: categoryName,
          options: opts,
          isDynamic: false,
          selectedCount,
        };
      });
  }

  private _initActorFilter(): void {
    this._initFilter({
      control: this.searchActorValuesCtrl,
      filterName: 'actor',
      filteredValues: this.filteredActorValues,
      loadingSubject: this.isActorLoading$,
      filterType: FilterTypesEnum.Actor,
    });
  }

  private _initAgentFilter(): void {
    this._initFilter({
      control: this.searchAgentValuesCtrl,
      filterName: 'agent',
      filteredValues: this.filteredAgentValues,
      loadingSubject: this.isAgentLoading$,
      filterType: FilterTypesEnum.AIAgentName,
      transformDisplayName: (text) => this._displayCamelCaseStringPipe.transform(text),
    });
  }

  private _initFilter(config: FilterInitConfig): void {
    const { control, filterName, filteredValues, loadingSubject, filterType, transformDisplayName } = config;

    control.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        debounceTime(DEFAULT_DEBOUNCE),
        tap(() => {
          filteredValues.length = 0;
          loadingSubject.next(true);
        }),
        switchMap((value) =>
          this._dataLayerHttpService.getAutocompleteOptions(value, filterName).pipe(
            catchError(() => of([])),
            finalize(() => {
              loadingSubject.next(false);
            })
          )
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((data: AutocompleteSearchResultsV2Model[]) => {
        for (const item of data) {
          filteredValues.push({
            id: item.id,
            value: item.id,
            displayName: transformDisplayName ? transformDisplayName(item.resultText) : item.resultText,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }
      });
  }
}
